import type { BoardSize } from "./constants";

/** Empty cell marker (the original used -1 while gems are being removed). */
export const EMPTY = -1;

/** Grid indexed as `grid[x][y]`, with x = column and y = row (0 at the top). */
export type Grid = number[][];

/** Size of a grid, read from its shape. Every grid on a board has the same size. */
export function gridSize(grid: Grid): BoardSize {
  return { width: grid.length, height: grid[0]?.length ?? 0 };
}

export const FLAG_HORIZONTAL = 1;
export const FLAG_VERTICAL = 2;

export interface Alignment {
  readonly x: number;
  readonly y: number;
  readonly horizontal: boolean;
  readonly length: number;
}

export interface Cell {
  readonly x: number;
  readonly y: number;
}

export function createGrid<T>(
  width: number,
  height: number,
  make: (x: number, y: number) => T,
): T[][] {
  return Array.from({ length: width }, (_column, x) =>
    Array.from({ length: height }, (_row, y) => make(x, y)),
  );
}

export function cloneGrid(grid: Grid): Grid {
  return grid.map((column) => column.slice());
}

function typeBit(type: number): number {
  return type >= 0 ? 1 << type : 0;
}

/**
 * Returns FLAG_HORIZONTAL and/or FLAG_VERTICAL when the gem at (x, y) is part of a run of at
 * least three equal gems in that direction (gweled_is_part_of_an_alignment).
 */
export function alignmentFlags(grid: Grid, x: number, y: number): number {
  const { width, height } = gridSize(grid);
  let result = 0;

  for (let i = x - 2; i <= x; i++) {
    if (i >= 0 && i + 2 < width) {
      if (typeBit(grid[i][y]) & typeBit(grid[i + 1][y]) & typeBit(grid[i + 2][y])) {
        result |= FLAG_HORIZONTAL;
        break;
      }
    }
  }

  for (let i = y - 2; i <= y; i++) {
    if (i >= 0 && i + 2 < height) {
      if (typeBit(grid[x][i]) & typeBit(grid[x][i + 1]) & typeBit(grid[x][i + 2])) {
        result |= FLAG_VERTICAL;
        break;
      }
    }
  }

  return result;
}

export function swapCells(grid: Grid, x1: number, y1: number, x2: number, y2: number): void {
  const tmp = grid[x1][y1];
  grid[x1][y1] = grid[x2][y2];
  grid[x2][y2] = tmp;
}

/**
 * Lists every run of three or more gems (check_for_alignments). Runs are collected column by
 * column for vertical ones and row by row for horizontal ones, in the same order as the original.
 */
export function findAlignments(grid: Grid): Alignment[] {
  const { width, height } = gridSize(grid);
  const list: Alignment[] = [];

  for (let i = 0; i < width; i++) {
    let count = 0;
    let startY = 0;
    for (let j = 0; j < height; j++) {
      if (alignmentFlags(grid, i, j) & FLAG_VERTICAL) {
        if (count === 0) startY = j;
        count++;
      } else {
        if (count > 2) list.push({ x: i, y: startY, horizontal: false, length: count });
        count = 0;
      }
    }
    if (count > 2) list.push({ x: i, y: startY, horizontal: false, length: count });
  }

  for (let j = 0; j < height; j++) {
    let count = 0;
    let startX = 0;
    for (let i = 0; i < width; i++) {
      if (alignmentFlags(grid, i, j) & FLAG_HORIZONTAL) {
        if (count === 0) startX = i;
        count++;
      } else {
        if (count > 2) list.push({ x: startX, y: j, horizontal: true, length: count });
        count = 0;
      }
    }
    if (count > 2) list.push({ x: startX, y: j, horizontal: true, length: count });
  }

  return list;
}

/**
 * Finds one swap that creates an alignment (check_for_moves_left). Returns the cell that the
 * move produces the alignment on, which the hint system bounces.
 *
 * Mirrors the original search order and bounds: left, right, up, down, for each cell in
 * row-major order. The swap is always undone before returning.
 */
export function findMove(grid: Grid): Cell | null {
  const { width, height } = gridSize(grid);
  for (let j = 0; j < height; j++) {
    for (let i = 0; i < width; i++) {
      if (i > 0) {
        swapCells(grid, i - 1, j, i, j);
        const found = alignmentFlags(grid, i, j) !== 0;
        swapCells(grid, i - 1, j, i, j);
        if (found) return { x: i - 1, y: j };
      }
      if (i < width - 1) {
        swapCells(grid, i + 1, j, i, j);
        const found = alignmentFlags(grid, i, j) !== 0;
        swapCells(grid, i + 1, j, i, j);
        if (found) return { x: i + 1, y: j };
      }
      if (j > 0) {
        swapCells(grid, i, j - 1, i, j);
        const found = alignmentFlags(grid, i, j) !== 0;
        swapCells(grid, i, j - 1, i, j);
        if (found) return { x: i, y: j - 1 };
      }
      if (j < height - 1) {
        swapCells(grid, i, j + 1, i, j);
        const found = alignmentFlags(grid, i, j) !== 0;
        swapCells(grid, i, j + 1, i, j);
        if (found) return { x: i, y: j + 1 };
      }
    }
  }
  return null;
}

export function isAdjacent(x1: number, y1: number, x2: number, y2: number): boolean {
  return (x1 === x2 && Math.abs(y1 - y2) === 1) || (y1 === y2 && Math.abs(x1 - x2) === 1);
}
